import { DateTime } from 'luxon'
import sharedMessages from '~/locales/common.translate'
import {
	ROLE_LEADER,
	ROLE_CO_LEADER,
	ROLE_ADMIN,
	ROLE_CAPTAIN,
	TOGGLE_TYPE_CHAMPIONSHIP,
	MAX_TH_LEVEL,
	THCUP_LEVELS,
	thcupToggleType,
	COOKIE_TOGGLE_TYPE,
} from '~/utils/constants'
import { getErrorMessage } from '~/utils/errors'

// eslint-disable-next-line max-lines-per-function
export default ({ app, store, redirect }, inject) => {
	function redirectToError(path) {
		const locale = store.state.i18n.locale
		redirect(app.localePath(path, locale))
		return null
	}

	inject('getLocale', () => {
		return store.state.i18n.locale
	})

	inject('getEventLink', () => {
		const locale = store.state.i18n.locale.toLowerCase()
		const baseUrl = 'https://athlos.gg/sc/redirect/coc/wf2025'

		if (locale === 'es') {
			return `${baseUrl}/es`
		} else if (locale === 'pt') {
			return `${baseUrl}/pt`
		} else if (locale === 'de') {
			return `${baseUrl}/de`
		} else if (locale === 'it') {
			return `${baseUrl}/it`
		} else if (locale === 'fr') {
			return `${baseUrl}/fr`
		} else if (locale === 'jp') {
			return `${baseUrl}/jp`
		} else if (locale === 'kr' || locale === 'ko') {
			return `${baseUrl}/kr`
		}
		return `${baseUrl}/en`
	})

	inject('getMemberRole', (role) => {
		app.i18n._vm.messages = sharedMessages
		if (role === ROLE_LEADER) {
			return app.i18n.t('common.strings.leader')
		} else if (role === ROLE_CO_LEADER) {
			return app.i18n.t('common.strings.coLeader')
		} else if (role === ROLE_ADMIN) {
			return app.i18n.t('common.strings.elder')
		} else {
			return app.i18n.t('common.strings.member')
		}
	})

	inject('goBack', () => {
		app.router.back()
	})

	inject('getOpenWebUrl', (url) => {
		if (!url) return null

		if (!store.state.inApp) return url

		return `clashofclans://OpenWeb?url=${encodeURIComponent(url)}`
	})

	inject('orderRosterByImportance', (items) => {
		return items.sort((a, b) => {
			if (a.rosterPosition === ROLE_CAPTAIN) {
				return -1
			} else if (b.rosterPosition === ROLE_CAPTAIN) {
				return 1
			} else if (a.role === ROLE_LEADER) {
				return -1
			} else if (b.role === ROLE_LEADER) {
				return 1
			} else if (a.role === ROLE_CO_LEADER) {
				return -1
			} else if (b.role === ROLE_CO_LEADER) {
				return 1
			} else if (a.townHallLevel > b.townHallLevel) {
				return -1
			} else if (a.townHallLevel < b.townHallLevel) {
				return 1
			} else if (a.name.toLowerCase() < b.name.toLowerCase()) {
				return -1
			} else if (a.name.toLowerCase() > b.name.toLowerCase()) {
				return 1
			}

			return 0
		})
	})

	inject('getStarsForCompetitorInMatchSeries', (matchSeries, competitorId) => {
		if (
			!matchSeries ||
			!matchSeries.matches ||
			matchSeries.matches.length < 1 ||
			!matchSeries.date ||
			!matchSeries.competitors ||
			matchSeries.competitors < 2
		) {
			return '-'
		}
		const periods = matchSeries.matches[0].periods
		if (!periods || periods.length < 1) {
			return '-'
		}
		const starPeriods = periods.find((p) => p.name === 'stars')
		if (!starPeriods || starPeriods.length < 1) {
			return '-'
		}

		const competitor = starPeriods.competitors.find((c) => c.id === competitorId)
		if (!competitor || !competitor.result) {
			return '-'
		}

		return competitor.result.score
	})

	function getPeriodScoreForCompetitor(matchSeries, competitorId, periodName) {
		if (!matchSeries || !matchSeries.matches || matchSeries.matches.length < 1) {
			return null
		}
		const periods = matchSeries.matches[0].periods
		if (!periods || periods.length < 1) {
			return null
		}
		const period = periods.find((p) => p.name === periodName)
		if (!period || !period.competitors) {
			return null
		}
		const competitor = period.competitors.find((c) => c.id === competitorId)
		if (!competitor || !competitor.result || typeof competitor.result.score !== 'number') {
			return null
		}
		return competitor.result.score
	}

	inject('getDestructionForCompetitorInMatchSeries', (matchSeries, competitorId) => {
		return getPeriodScoreForCompetitor(matchSeries, competitorId, 'destruction')
	})

	inject('getDurationForCompetitorInMatchSeries', (matchSeries, competitorId) => {
		return getPeriodScoreForCompetitor(matchSeries, competitorId, 'duration')
	})

	inject('tryCatchWrapper', async (fn) => {
		try {
			const data = await fn
			return [data, null]
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error(err)
			return [null, err]
		}
	})

	inject(
		'checkInForMatch',
		// eslint-disable-next-line complexity, max-lines-per-function
		async (matchId, selectedPlayers = []) => {
			const isChinese = store.state.isChinese

			if (store.state.globalCreateWarLoading) {
				return
			}

			store.commit('setGlobalCreateWarLoading', true)

			let response = null
			if (selectedPlayers.length) {
				response = await checkPlayersMonthlyQualifier(selectedPlayers)
			} else {
				response = await checkPlayersMonthlyFinals()
			}

			if (!response) {
				return null
			}

			try {
				const { data: match } = await app.$api.matches.matchSeries.get(matchId)

				const matchDate = DateTime.fromISO(match.date).toUTC()
				const now = DateTime.utc()

				if (matchDate < now) {
					const errorMessage = `No checkin done as it's passed match time`
					await app.$logApi.createLogMessage(errorMessage, {
						matchId,
					})
					store.commit('match/setShouldCheckin', false)
					store.commit('error/setLatestError', errorMessage)
					return redirectToError('/error')
				}

				const { data: checkins } = await app.$api.matches.checkins.getByMatchSeriesId(
					matchId
				)

				if (checkins.length > 0) {
					const myCheckIn = checkins.find((c) => c?.user?.id === store.state.user.userId)
					if (myCheckIn) {
						const errorMessage = `No checkin done as the user is already checked in`
						await app.$logApi.createLogMessage(errorMessage, {
							matchId,
						})
						store.commit('match/setShouldCheckin', false)
						return null
					}
				}

				const warData = await app.$warApi.getWarDeeplink(matchId, store.state.scToken)
				return warData.data.link
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error(err)

				const statusCode = err?.response?.status || 0
				const errorPath = getErrorMessage(err, false, isChinese)

				await app.$logApi.createLogMessage(`Checkin failed`, {
					error: JSON.stringify(err),
					matchId,
				})

				if (errorPath) {
					return redirectToError(errorPath)
				}

				if (statusCode === 403) {
					const errorCode = isChinese ? '/error/21' : '/error/22'
					return redirectToError(errorCode)
				}

				const errorCode = isChinese ? '/error/12' : '/error/13'
				return redirectToError(errorCode)
			} finally {
				store.commit('setGlobalCreateWarLoading', false)
			}

			async function checkPlayersMonthlyFinals() {
				try {
					const data = await app.$scApi.checkIfClanCanCheckIn(store.state.scToken)

					if (!data || data.status !== 200) {
						await app.$logApi.createLogMessage(
							`checkinForMatch clan check failed - didn't return 200`,
							{
								error: JSON.stringify(data),
							}
						)

						store.commit('setGlobalCreateWarLoading', false)
						return redirectToError('/error')
					}

					return true
				} catch (err) {
					// eslint-disable-next-line no-console
					console.error(err)

					const errorMessage = err?.response?.data?.errors || 'unknown'
					const logMessage = `checkForCorrectClanSettings failed - ${errorMessage}`
					let errorPath = getErrorMessage(err, false)
					if (!errorPath) {
						errorPath = isChinese ? '/error/12' : '/error/13'
					}

					await app.$logApi.createLogMessage(logMessage, {
						error: JSON.stringify(err),
					})

					store.commit('setGlobalCreateWarLoading', false)
					return redirectToError(errorPath)
				}
			}

			async function checkPlayersMonthlyQualifier(selectedPlayers) {
				try {
					await app.$warApi.setSelectedPlayers(
						store.state.scToken,
						store.state.user.clan.tag,
						selectedPlayers
					)
				} catch (err) {
					// eslint-disable-next-line no-console
					console.error(err)
					store.commit('setGlobalCreateWarLoading', false)
					store.commit('match/setShouldCheckin', false)
					return redirectToError('/error/27')
				}

				try {
					await app.$scApi.checkSelectedPlayers(store.state.scToken)
				} catch (err) {
					// eslint-disable-next-line no-console
					console.error(err)
					store.commit('setGlobalCreateWarLoading', false)
					store.commit('match/setShouldCheckin', false)
					const errorPath = getErrorMessage(err)
					return redirectToError(errorPath)
				}

				return true
			}
		}
	)

	inject('getThLevels', () => {
		return (
			store.state?.event?.segments
				?.filter((s) => !s.name.includes('[TEST]'))
				?.map((s) => s.configuration?.thLevel)
				?.filter(Boolean) || []
		)
	})

	inject('getRulesLocale', () => {
		const locale = store.state?.i18n?.locale?.toUpperCase() || 'EN'
		return locale === 'JP' ? 'JA' : locale
	})

	inject('loadCountrySettings', async () => {
		try {
			// in case of supercell send the country code
			if (store.state.country) {
				return
			}

			// in case of it was loaded before
			const cookieCountry = app.$cookies.get('coc-country')
			if (cookieCountry) {
				store.commit('setCountry', cookieCountry)
				return
			}

			// load the country code from cloudflare
			const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace')
			const data = await response.text()
			const json = Object.fromEntries(
				data
					.trim()
					.split('\n')
					.map((line) => line.split('='))
			)
			const countryCode = json.loc || 'XX'

			app.$cookies.set('coc-country', countryCode, {
				expires: DateTime.utc().plus({ minutes: 30 }).toJSDate(),
				path: '/',
			})

			store.commit('setCountry', countryCode)
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error)
		}
	})

	function isMaxThLevel(thLevel = undefined) {
		if (!thLevel) {
			thLevel = store.state.event.activeSegment?.configuration?.thLevel || MAX_TH_LEVEL
		}
		return thLevel === MAX_TH_LEVEL
	}
	inject('isMaxThLevel', isMaxThLevel)

	function getHomeTabByThLevel(thLevel = undefined) {
		if (!thLevel) {
			thLevel = store.state.event.activeSegment?.configuration?.thLevel || MAX_TH_LEVEL
		}

		// Land players on their own town hall cup when eligible (TH 9/12/15);
		// everyone else (TH18, in-between levels, no clan) defaults to the
		// World Championship tab.
		let defaultTab = TOGGLE_TYPE_CHAMPIONSHIP
		if (THCUP_LEVELS.includes(thLevel)) {
			defaultTab = thcupToggleType(thLevel)
		}
		return defaultTab
	}
	inject('getHomeTabByThLevel', getHomeTabByThLevel)

	function setCookieDefaultHomeTab({ defaultTab, thLevel } = {}) {
		if (!defaultTab) {
			defaultTab = getHomeTabByThLevel(thLevel)
		}

		app.$cookies.set(COOKIE_TOGGLE_TYPE, defaultTab, {
			expires: DateTime.now().plus({ days: 365 }).toJSDate(),
			path: '/',
		})
	}
	inject('setCookieDefaultHomeTab', setCookieDefaultHomeTab)

	function getClanExternalId() {
		const externalIds = store.state.user?.latestRoster?.externalIds || []
		const externalId = externalIds.find((e) => e.provider === 'sc_coc_clan_tag')
		return externalId?.value
	}
	inject('getClanExternalId', getClanExternalId)
}
